import React, { useMemo } from 'react';
import { Popup } from 'semantic-ui-react';
import classNames from 'classnames';
import { ErrorSettings } from '../../../../types/template-types';
import { useAppSelector } from '../../../../hooks/ReduxHooks';
import { usePopupOffset } from './hooks/usePopupOffset';
import { usePopupPositioning } from './hooks/usePopupPositioning';
import {
  calculateActualScreenXPosition,
  calculateTriangleSize,
  calculateErrorDisplay,
  calculateActualScreenYPosition,
  isMobileDevice
} from './helpers/popupHelpers';
import './FieldValidationPopup.scss';
import { useZoomLevel } from '../../../../templateeditor/components/ZoomLevelProvider';

type FieldValidationPopupProps = {
  className?: string;
  fieldId?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  errorSettings?: ErrorSettings;
  fieldType?: number;
  focused?: boolean;
  highlighted?: boolean;
  isMissingRequired?: boolean;
  isCompleted?: boolean;
  containsErrors?: boolean;
  showValidationPopup?: boolean;
};

export default function FieldValidationPopup(props: FieldValidationPopupProps) {
  const { fieldId, x, y, width, height, errorSettings, focused, highlighted, isMissingRequired, isCompleted, containsErrors, showValidationPopup } = props;

  const page = useAppSelector((state) => state.formeditor.template?.pages?.find((p) => p.fieldPositions?.some((fp) => fp.fieldId === fieldId)));
  const fieldPosition = page?.fieldPositions?.find((fp) => fp.fieldId === fieldId);

  const zoomLevel = useZoomLevel();
  const actualScreenX = useMemo(() => calculateActualScreenXPosition(x, page, fieldPosition, zoomLevel), [x, page, fieldPosition, zoomLevel]);
  const actualScreenY = useMemo(() => calculateActualScreenYPosition(y, page, fieldPosition, zoomLevel), [y, page, fieldPosition, zoomLevel]);
  const triangleConfig = useMemo(() => calculateTriangleSize(width, height), [width, height]);
  const errorConfig = useMemo(() => calculateErrorDisplay(errorSettings, isMissingRequired), [errorSettings, isMissingRequired]);
  const isMobile = isMobileDevice();

  const positioning = usePopupPositioning({
    x,
    y,
    width,
    actualScreenX,
    actualScreenY,
    screenWidth: window.innerWidth,
    errorMessage: errorConfig.errorMessage,
    pageNumber: page?.number,
    isMobile: !!isMobile
  });

  const offset = usePopupOffset({
    width,
    height,
    placement: positioning.placement,
    shouldShowPopupLeft: positioning.shouldShowPopupLeft,
    screenWidth: window.innerWidth,
    isMobile: !!isMobile
  });

  if (!containsErrors || isCompleted) {
    return null;
  }

  return (
    <Popup
      className={classNames('error-popup')}
      content={errorConfig.errorMessage}
      offset={offset}
      open={showValidationPopup === false ? false : focused || highlighted}
      pinned
      position={positioning.popupPosition}
      style={{
        borderLeftColor: errorConfig.borderColor
      }}
      trigger={<polygon fill={errorConfig.borderColor} fillOpacity={1} opacity={1} points={triangleConfig.polygonPoints} />}
    />
  );
}
